/**
 * Glasses/contact-lens Rx generation-and-release (`BACKLOG.md` M10). The FTC Eyeglass Rule and Contact
 * Lens Rule make the finished Rx a distinct, semi-regulated document a patient (or any authorized
 * third party they designate) is entitled to receive on request, unconditioned on a purchase —
 * `RELEASE_STATUSES`/`RELEASE_METHODS` below model that release event, not a purchase-gated hand-off.
 *
 * **`defaultRxExpiration` is UNREVIEWED.** Neither FTC rule itself sets a fixed federal expiration —
 * the one-year figure below is a common state-optometry-board default (many states require a minimum
 * one-year Rx validity absent an explicit shorter period the prescriber states), not independently
 * confirmed against any specific state's board rules or against a primary FTC source during this
 * build. Treat this as a reasonable placeholder needing real per-state confirmation before being relied
 * on for an actual issued Rx, matching this codebase's own established UNREVIEWED convention
 * (`dermatology/phototherapy.ts`, `physicalTherapy/plansOfCare.ts`).
 */

import { CALENDAR_DATE_PATTERN } from '../calendarSync.ts';

export const RX_TYPES = ['glasses', 'contact_lens'] as const;
export type RxType = (typeof RX_TYPES)[number];

export function isRxType(value: string): value is RxType {
  return (RX_TYPES as readonly string[]).includes(value);
}

export const RELEASE_STATUSES = ['not_released', 'released'] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

export function isReleaseStatus(value: string): value is ReleaseStatus {
  return (RELEASE_STATUSES as readonly string[]).includes(value);
}

/** How the Rx actually reached the patient/authorized third party — free-text-adjacent but closed, so
 * a later report ("was this ever handed to the patient directly vs. only faxed to a retailer") is a
 * real query, not a guess against unstructured text. */
export const RELEASE_METHODS = [
  'handed_to_patient',
  'faxed',
  'emailed',
  'verified_verbal',
] as const;
export type ReleaseMethod = (typeof RELEASE_METHODS)[number];

export function isReleaseMethod(value: string): value is ReleaseMethod {
  return (RELEASE_METHODS as readonly string[]).includes(value);
}

export const DEFAULT_RX_VALIDITY_YEARS = 1;

/**
 * `issuedOn + DEFAULT_RX_VALIDITY_YEARS` years, as a calendar date — pure, no clock. UTC arithmetic on
 * a `YYYY-MM-DD` string, never a `Date`-as-instant, matching `orthopedics/globalSurgicalPeriod.ts`'s
 * own `addDaysToCalendarDate` discipline in spirit — but adds years, not days, since Rx validity is
 * expressed in whole years everywhere this was checked.
 *
 * A February 29 issuance whose target year is not a leap year has no such date to land on —
 * `setUTCFullYear` rolls that case forward to March 1 rather than producing an invalid calendar date
 * (`2029-02-29` does not exist). No source consulted during this build states which convention is
 * correct for an Rx specifically; rolling forward, not back, was chosen only because it is the more
 * common calendar-arithmetic default, not because it was confirmed correct for this specific document
 * type. UNREVIEWED like the rest of this function — see the module header.
 */
export function defaultRxExpiration(issuedOn: string): string {
  if (!CALENDAR_DATE_PATTERN.test(issuedOn)) {
    throw new Error(`not a calendar date (YYYY-MM-DD): ${issuedOn}`);
  }
  const [yearStr, monthStr, dayStr] = issuedOn.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const asDate = new Date(Date.UTC(year, month - 1, day));
  asDate.setUTCFullYear(asDate.getUTCFullYear() + DEFAULT_RX_VALIDITY_YEARS);
  const resultYear = asDate.getUTCFullYear();
  const resultMonth = String(asDate.getUTCMonth() + 1).padStart(2, '0');
  const resultDay = String(asDate.getUTCDate()).padStart(2, '0');
  return `${String(resultYear)}-${resultMonth}-${resultDay}`;
}
